import * as React from 'react';
import { cn } from '@/hen-ui/utils';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import {
  ProgressGroupContext,
  type ProgressGroupContextValue,
  type ProgressLabelAlign,
} from '@/hen-ui/atomics';

export const PROGRESS_GROUP_ROW_GAPS = ['min', 'sm', 'md', 'lg'] as const;
export type ProgressGroupRowGap = (typeof PROGRESS_GROUP_ROW_GAPS)[number];

const rowGapClass: Record<ProgressGroupRowGap, string> = {
  min: 'gap-y-min',
  sm: 'gap-y-sm',
  md: 'gap-y-md',
  lg: 'gap-y-lg',
};

export interface ProgressGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  /** Vertical spacing between rows. */
  rowGap?: ProgressGroupRowGap;
  /** Default label alignment for every row; a child Progress may override it. */
  labelAlign?: ProgressLabelAlign;
  /** A set of `Progress` bars. */
  children: React.ReactNode;
}

/**
 * Lays out `Progress` bars on a two-column grid so labels share one `max-content` column
 * and all tracks start on the same line — authors never set a per-bar width.
 */
export const ProgressGroup = React.forwardRef<
  HTMLDivElement,
  ProgressGroupProps
>(({ rowGap = 'sm', labelAlign, children, ...props }, ref) => {
  const contextValue = React.useMemo<ProgressGroupContextValue>(
    () => ({ grouped: true, labelAlign }),
    [labelAlign],
  );
  const resolvedRowGap = PROGRESS_GROUP_ROW_GAPS.includes(rowGap)
    ? rowGap
    : 'sm';

  return (
    <ProgressGroupContext.Provider value={contextValue}>
      <div
        {...props}
        ref={ref}
        data-hen-id={COMPONENT_IDS.ProgressGroup}
        className={cn(
          'grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-sm',
          rowGapClass[resolvedRowGap],
        )}
      >
        {children}
      </div>
    </ProgressGroupContext.Provider>
  );
});
ProgressGroup.displayName = 'ProgressGroup';
